import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface ShoppingItem {
  id: string;
  oil_id: string;
  oil_name: string;
  item_type: string;
  reason: string | null;
  notes: string | null;
  priority: number;
  is_purchased: boolean;
  created_at: string;
}

export function useShoppingList() {
  const queryClient = useQueryClient();
  const { user, isAuthenticated } = useAuth();

  const { data: items = [], isLoading, error } = useQuery({
    queryKey: ["shopping-list", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from("shopping_list")
        .select("*")
        .eq("user_id", user.id)
        .order("priority", { ascending: false })
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as ShoppingItem[];
    },
    enabled: isAuthenticated,
    staleTime: 2 * 60 * 1000,
  });

  const addItemMutation = useMutation({
    mutationFn: async ({ oil_id, oil_name, reason, priority, item_type }: { oil_id: string; oil_name: string; reason?: string; priority?: number; item_type?: string }) => {
      if (!user?.id) throw new Error("User not authenticated");

      // Check if already exists
      const existing = items.find(item => item.oil_id === oil_id && !item.is_purchased);
      if (existing) {
        // Update reason if provided
        if (reason) {
          await supabase
            .from("shopping_list")
            .update({ reason })
            .eq("id", existing.id);
        }
        return existing;
      }

      const { data, error } = await supabase
        .from("shopping_list")
        .insert({
          user_id: user.id,
          oil_id,
          oil_name,
          item_type: item_type || "essential_oil",
          reason: reason || null,
          priority: priority ?? 0,
          is_purchased: false,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shopping-list"] });
    },
  });

  const updateItemMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<ShoppingItem> }) => {
      const { error } = await supabase
        .from("shopping_list")
        .update(updates)
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shopping-list"] });
    },
  });

  const removeItemMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("shopping_list")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shopping-list"] });
    },
  });

  const togglePurchasedMutation = useMutation({
    mutationFn: async (id: string) => {
      const item = items.find(i => i.id === id);
      if (!item) return;

      const { error } = await supabase
        .from("shopping_list")
        .update({ is_purchased: !item.is_purchased })
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shopping-list"] });
    },
  });

  const clearPurchasedMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) return;

      const { error } = await supabase
        .from("shopping_list")
        .delete()
        .eq("user_id", user.id)
        .eq("is_purchased", true);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shopping-list"] });
    },
  });

  const pendingItems = items.filter(i => !i.is_purchased);
  const purchasedItems = items.filter(i => i.is_purchased);

  // Helper function to set priority
  const setPriority = (id: string, priority: number) => {
    updateItemMutation.mutate({ id, updates: { priority } });
  };

  return {
    items,
    pendingItems,
    purchasedItems,
    isLoading,
    error,
    addItem: addItemMutation.mutate,
    updateItem: updateItemMutation.mutate,
    removeItem: removeItemMutation.mutate,
    togglePurchased: togglePurchasedMutation.mutate,
    clearPurchased: clearPurchasedMutation.mutate,
    setPriority,
    isAdding: addItemMutation.isPending,
    isUpdating: updateItemMutation.isPending,
    isInList: (oilId: string) => items.some(i => i.oil_id === oilId && !i.is_purchased),
  };
}
